import React, { useEffect, useState } from 'react';
import { LomavisBadgeList1 } from '@/lomavisSharedUi/molecules/badges/LomavisBadgeList1/LomavisBadgeList1';
import {
  LomavisTagSelector1,
  TagOption
} from '@/lomavisSharedUi/molecules/inputs/LomavisTagSelector1';
import { Button, Modal } from 'react-bootstrap';
import { translateComponent } from '@/utils/tolgee/translateComponent';

interface LomavisTagListAndSelector1Props {
  /** Initially available tags */
  availableTags: TagOption[];

  /** Initially selected tags */
  initialSelected?: TagOption[];

  /** Max number of badges to display before showing +N */
  maxVisible?: number;
  /** NEW: max allowed number of tags */
  maxAllowedTags?: number;

  /** Called when tags are changed */
  onTagsChange?: (tags: TagOption[]) => void;

  /** Called when a new tag is created */
  onCreateTag?: (tagName: string) => Promise<TagOption> | TagOption;
  /** Function to delete tags from available tags */
  deleteTag?: (id: string) => void;

  className?: string;
  direction?: 'top' | 'left';
  /** Whether to allow creating new tags */
  allowCreate?: boolean;
  /** Customizable text props */
  addTagsText?: string;
  createNewTagsText?: string;
  availableTagsText?: string;
  noTagsAvailableText?: string;
  allTagsSelected?: string;
  loadPost?: () => void;
}

/**
 * Organisms: LomavisTagListAndSelector1
 */
export const LomavisTagListAndSelector1: React.FC<
  LomavisTagListAndSelector1Props
> = ({
  availableTags,
  initialSelected = [],
  maxVisible = 3,
  maxAllowedTags,
  onTagsChange,
  onCreateTag,
  className = '',
  direction = 'top',
  deleteTag,
  addTagsText,
  createNewTagsText,
  availableTagsText,
  noTagsAvailableText,
  allTagsSelected,
  allowCreate,
  loadPost
}) => {
  const t = translateComponent('LomavisTagListAndSelector1');
  const [selectedTags, setSelectedTags] =
    useState<TagOption[]>(initialSelected);

  const [showConfirm, setShowConfirm] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<TagOption | null>(null);

  const handleTagsChange = (tags: TagOption[]) => {
    setSelectedTags(tags);
    onTagsChange?.(tags);
  };

  const handleDeleteTag = (id: string) => {
    const updated = selectedTags.filter((tag) => tag.uuid !== id);
    setSelectedTags(updated);
    onTagsChange?.(updated);
  };
  useEffect(() => {
    setSelectedTags(initialSelected);
  }, [initialSelected]);

  const confirmAndDeleteTag = (id: string) => {
    const tag = availableTags.find((t) => t.uuid === id);
    setTagToDelete(tag || null);
    setShowConfirm(true);
  };

  const confirmDelete = () => {
    if (tagToDelete) {
      deleteTag?.(tagToDelete.uuid);
    }
    setTagToDelete(null);
    setShowConfirm(false);
  };

  const cancelDelete = () => {
    setTagToDelete(null);
    setShowConfirm(false);
  };

  return (
    <div
      className={`d-flex gap-2 ${
        direction === 'left' ? 'flex-row' : 'flex-column'
      } ${className}`}
    >
      {/* Selected tag list */}
      {selectedTags?.length > 0 && (
        <LomavisBadgeList1
          items={selectedTags.map((t) => ({
            id: t.uuid,
            title: t.name,
            color: t.color
          }))}
          maxVisible={maxVisible}
          onDelete={handleDeleteTag}
        />
      )}

      {/* Selector */}
      <div style={{ maxWidth: '500px' }}>
        <LomavisTagSelector1
          selectedTags={selectedTags}
          availableTags={availableTags}
          onTagsChange={handleTagsChange}
          onCreateTag={onCreateTag}
          maxAllowedTags={maxAllowedTags}
          {...(deleteTag ? { deleteTag: confirmAndDeleteTag } : {})}
          {...(loadPost ? { loadPost } : {})}
          addTagsText={addTagsText}
          createNewTagsText={createNewTagsText}
          availableTagsText={availableTagsText}
          noTagsAvailableText={noTagsAvailableText}
          allTagsSelected={allTagsSelected}
          allowCreate={allowCreate}
        />{' '}
      </div>

      {/* Bootstrap Modal */}
      <Modal show={showConfirm} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirmDelete')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('areYouSure')}?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete}>
            {t('cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            {t('delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
